import React, { FC, ReactNode, useEffect, useState } from 'react';
import { DashboardContext, PaymentActionType } from './context';
import { STORAGE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { SidebarItemType } from 'components/sidebar/types';
import { getFirstDayOfMonth, getLastDayOfMonth, handleApiError } from 'utils/functions';
import { CoKitchenType } from 'models/cokitchen.model';
import { BranchAddressType } from 'models/branch-address.model';
import { CompaniesListType, OnboardingType } from 'models/company.model';
import { PaymentCardType, PaystackPropsType } from 'models/payment.model';
import { MemberListType } from 'models/member.model';
import { DashboardMonthlyStatsType } from 'models/statements.model';
import { LocationObjectType } from 'models/location.model';
import { CalculatedOrderType } from 'models/order.model';
import { CompanyWalletType } from 'models/wallet.model';

const DashboardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isBackLink, setIsBackLink] = useState(false);
  const [backLinkTitle, setBackLinkTitle] = useState('');
  const [backLink, setBackLink] = useState('');
  const [hideSidebar, setHideSidebar] = useState(false);
  const [needHelp, setNeedHelp] = useState(false);
  const [activeCompany, setActiveCompany] = useState({} as CompaniesListType);
  const [companyList, setCompanyList] = useState([] as CompaniesListType[]);
  const [paystackProps, setPaystackProps] = useState({
    publicKey: PAYSTACK_PUBLIC_KEY,
    channels: ['card'],
    metadata: {
      customer: {
        order: false,
        webhook: true,
        phone_number: activeCompany?.user?.phone_number,
        body: { company_id: activeCompany.id }
      }
    }
  } as PaystackPropsType);
  const [activePaymentCard, setActivePaymentCard] = useState<PaymentCardType | null>(null);
  const [paymentView, setPaymentView] = useState<'methods' | 'pay'>('methods');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentCards, setPaymentCards] = useState([] as PaymentCardType[]);
  const [paymentType, setPaymentType] = useState<PaymentActionType | null>(null);
  const [members, setMembers] = useState<MemberListType[]>([]);
  const [monthlyStat, setMonthlyStat] = useState<DashboardMonthlyStatsType>(
    {} as DashboardMonthlyStatsType
  );
  const [monthlyStatLoading, setMonthlyStatLoading] = useState(false);
  const [sidebarMenu, setSidebarMenu] = useState<SidebarItemType[]>([]);
  const [basketValue, setBasketValue] = useState(0);

  const [activeCokitchen, setActiveCokitchen] = useState({} as CoKitchenType);
  const [addressListLoading, setAddressListLoading] = useState(false);
  const [savedAddressList, setSavedAddressList] = useState<LocationObjectType[]>([]);
  const [isBasketDisabled, setIsBasketDisabled] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState<LocationObjectType | null>(null);
  const [defaultAddressLoading, setDefaultAddressLoading] = useState(false);
  const [cokitchenList, setCokitchenList] = useState<CoKitchenType[]>([]);
  const [calculatedOrder, setCalculatedOrder] = useState({} as CalculatedOrderType);
  const [companyWalletDetails, setCompanyWalletDetails] = useState<CompanyWalletType>();
  const [companyBudget, setCompanyBudget] = useState('');
  const [branchAddresses, setBranchAddresses] = useState<BranchAddressType[]>([]);
  const [branchAddressesLoading, setBranchAddressesLoading] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingType[]>([]);

  const getBranchAddresses = async () => {
    setBranchAddressesLoading(true);
    try {
      const res = await apiService.get(`branches/${activeCompany?.id}`);
      setBranchAddresses(res.data.data);
    } catch (err) {
      console.log(err);
    } finally {
      setBranchAddressesLoading(false);
    }
  };

  const getCompanyBudget = async () => {
    try {
      const res = await apiService.get(`admin/budget/${activeCompany?.id}`);
      setCompanyBudget(res.data.data.total_budget);
    } catch (err) {
      console.log(err);
    }
  };

  const getCokitchenList = async () => {
    const url = 'internal/cokitchens';
    const res = await apiService.get(url, {
      baseURL: process.env.REACT_APP_CORE_API_URL,
      isInternal: true
    });
    const data = res.data.data;
    setCokitchenList(data);
    return data;
  };

  const getSavedAddress = async () => {
    setAddressListLoading(true);
    try {
      const res = await apiService.get(`user-saved-address`, {
        baseURL: process.env.REACT_APP_CORE_API_URL
      });
      setSavedAddressList(res?.data?.data);
    } finally {
      setAddressListLoading(false);
    }
  };

  const getCompanyWalletDetails = async () => {
    try {
      const res = await apiService.get(`company/wallet/${activeCompany.id}`);
      setCompanyWalletDetails(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const getMonthlyStat = async () => {
    if (!activeCompany.id) return;
    try {
      setMonthlyStatLoading(true);
      const res = await apiService.get('admin/dashboard', {
        params: {
          start_date: getFirstDayOfMonth(),
          end_date: getLastDayOfMonth(),
          company_id: activeCompany.id
        }
      });
      const { data } = res.data;
      setMonthlyStat(data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setMonthlyStatLoading(false);
    }
  };

  const getMembers = async () => {
    if (!activeCompany?.id) return;
    try {
      const res = await apiService.get(
        `member?company_id=${activeCompany.id}&length=${Number.MAX_SAFE_INTEGER}`
      );
      if (res) {
        setMembers(res?.data?.data?.results ?? []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getPaymentCards = async () => {
    if (!activeCompany?.id) return;
    try {
      const res = await apiService.get(`billing/card/${activeCompany?.id}`);
      const sortedCards = res.data.data.sort((a: PaymentCardType) => (a?.is_primary ? -1 : 1));
      setPaymentCards(sortedCards);
    } catch (err) {
      console.log(err);
    }
  };

  const refetchCompanyList = async () => {
    try {
      const res = await apiService.get('company');
      setCompanyList(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const refetchSpecificCompany = async (id: string) => {
    try {
      const { data } = await apiService.get(`company/${id}`);

      setCompanyList((companyList) => {
        const index = companyList.findIndex((company) => company.id === id);
        if (index > -1) {
          companyList[index] = data.data;
        }
        return companyList;
      });

      return data.data;
    } catch (err) {
      console.log(err);
    }
  };

  const refetchActiveCompany = async () => {
    if (activeCompany?.id) {
      const data = await refetchSpecificCompany(activeCompany.id);
      setActiveCompany(data);
    }
  };

  const refetchOnboarding = async () => {
    try {
      if (!activeCompany.id) return;
      const res = await apiService.get(`company/${activeCompany.id}/onboarding`);
      setOnboarding(res.data.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  const getDefaultAddress = async () => {
    setDefaultAddressLoading(true);
    try {
      const res = await apiService.get('get_default_address', {
        baseURL: process.env.REACT_APP_CORE_API_URL
      });

      setDefaultAddress(res.data.defaultAddress);
    } finally {
      setDefaultAddressLoading(false);
    }
  };

  useEffect(() => {
    if (activeCompany?.id) {
      getPaymentCards();
      getMonthlyStat();
      getMembers();
      getSavedAddress();
      getDefaultAddress();
      getCokitchenList();
      getCompanyWalletDetails();
      getCompanyBudget();
      getBranchAddresses();
      setPaystackProps({
        publicKey: paystackProps.publicKey,
        channels: paystackProps.channels,
        amount: +(paymentAmount || 0) * 100,
        email: localStorage.getItem(STORAGE_KEYS.EMAIL) ?? '',
        metadata: {
          customer: {
            webhook: true,
            phone_number: activeCompany?.user?.phone_number,
            order: paystackProps?.metadata?.customer?.order,
            body: paystackProps?.metadata?.customer?.body
          }
        }
      });
    }
  }, [activeCompany, paymentAmount]);

  useEffect(() => {
    if (activeCompany?.id) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_COMPANY, activeCompany.id);
      localStorage.setItem(STORAGE_KEYS.ADMIN_SKIPPED, activeCompany.skip ? 'YES' : 'NO');
    }
  }, [activeCompany]);

  useEffect(() => {
    if (companyList.length > 0) {
      const localCompanyId = localStorage.getItem(STORAGE_KEYS.ACTIVE_COMPANY);
      const parsedActiveCompany = companyList.find((c) => c.id === localCompanyId);
      if (parsedActiveCompany?.id) {
        setActiveCompany(parsedActiveCompany);
      } else {
        setActiveCompany(companyList[0]);
      }
    }
  }, [companyList]);

  return (
    <DashboardContext.Provider
      value={{
        monthlyStat,
        setMonthlyStat,
        monthlyStatLoading,
        setMonthlyStatLoading,
        activePaymentCard,
        setActivePaymentCard,
        paymentView,
        setPaymentView,
        paymentAmount,
        setPaymentAmount,
        paymentCards,
        setPaymentCards,
        paymentType,
        setPaymentType,
        paymentLoading,
        setPaymentLoading,
        showMobileSidebar,
        setShowMobileSidebar,
        showFeedbackModal,
        setShowFeedbackModal,
        isBackLink,
        setIsBackLink,
        backLinkTitle,
        setBackLinkTitle,
        hideSidebar,
        setHideSidebar,
        backLink,
        setBackLink,
        activeCokitchen,
        setActiveCokitchen,
        needHelp,
        setNeedHelp,
        activeCompany,
        setActiveCompany,
        companyList,
        setCompanyList,
        members,
        setMembers,
        paystackProps,
        setPaystackProps,
        refetchCompanyList,
        refetchSpecificCompany,
        refetchActiveCompany,
        refetchMonthlyStat: getMonthlyStat,
        refetchMembers: getMembers,
        refetchPaymentCards: getPaymentCards,
        setSidebarMenu,
        sidebarMenu,
        basketValue,
        setBasketValue,
        addressListLoading,
        setAddressListLoading,
        savedAddressList,
        setSavedAddressList,
        refetchSavedAddressList: getSavedAddress,
        isBasketDisabled,
        setIsBasketDisabled,
        defaultAddress,
        setDefaultAddress,
        refetchDefaultAddress: getDefaultAddress,
        defaultAddressLoading,
        cokitchenList,
        calculatedOrder,
        setCalculatedOrder,
        companyWalletDetails,
        refetchCompanyWalletDetails: getCompanyWalletDetails,
        companyBudget,
        branchAddresses,
        branchAddressesLoading,
        refetchBranchAddresses: getBranchAddresses,
        onboarding,
        setOnboarding,
        refetchOnboarding
      }}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
